import React from "react";
import { convertUtcToFormattedTime } from "../../../../util/AppUtils";

const RevisionSelection = ({
  revision,
  setRevision,
  revisionData,
  myRevision,
  currentRev,
}) => {
  // State for selected revision (single selection with radio buttons)
  const [selectedRevision, setSelectedRevision] = React.useState(null);

  // Sync selectedRevision with revision prop
  React.useEffect(() => {
    if (revision && selectedRevision?.value !== revision) {
      // Find the label for the current revision
      const currentLabel = revision.includes("Major") ? `${revision} (Major)` : 
                          revision.includes("Minor") ? `${revision} (Minor)` : 
                          revision.includes("Amend") ? `${revision} (Amend)` : 
                          `${revision} (Major)`;
      setSelectedRevision({ value: revision, label: currentLabel });
    }
  }, [revision]);
  // Parse revision function (extracted from main component)
  const parseRevision = (rev) => {
    if (!rev) return null;
    
    // Handle special cases
    if (rev === "A-") {
      return { base: "A", minorNum: 0, amendChar: "", type: "major" };
    }
    
    // Handle major revisions (A-, B-, etc.)
    if (rev.endsWith("-")) {
      const base = rev.slice(0, -1);
      return { base, minorNum: 0, amendChar: "", type: "major" };
    }
    
    // Handle minor revisions (A1, A2, etc.)
    const minorMatch = rev.match(/^([A-Z]+)(\d+)$/);
    if (minorMatch) {
      return {
        base: minorMatch[1],
        minorNum: parseInt(minorMatch[2], 10),
        amendChar: "",
        type: "minor",
      };
    }
    
    // Handle amendments (A1a, A1b, etc.)
    const amendMatch = rev.match(/^([A-Z]+)(\d+)([a-z]+)$/);
    if (amendMatch) {
      return {
        base: amendMatch[1],
        minorNum: parseInt(amendMatch[2], 10),
        amendChar: amendMatch[3],
        type: "amend",
      };
    }
    
    // Handle minorless amendments (A-a, A-b, etc.)
    const amendMinorlessMatch = rev.match(/^([A-Z]+)-([a-z]+)$/);
    if (amendMinorlessMatch) {
      return {
        base: amendMinorlessMatch[1],
        minorNum: 0,
        amendChar: amendMinorlessMatch[2],
        type: "amend_minorless",
      };
    }
    
    return null;
  };

  // Can create revision function (simplified version)
  const canCreateRevision = (currentRevision, allRevisions) => {
    if (!currentRevision || !allRevisions || allRevisions.length === 0) {
      return { canCreate: true, reason: "No revisions exist, can create any revision type" };
    }

    const currentRevParsed = parseRevision(currentRevision.rev);
    if (!currentRevParsed) {
      return { canCreate: false, reason: "Invalid current revision format" };
    }

    // Check if current revision is rejected
    const currentStatus = currentRevision.status_changes
      ?.find((sc) => sc.is_latest)
      ?.status_changed_to?.toLowerCase();

    if (currentStatus === "rejected") {
      return { canCreate: false, reason: "Cannot create revisions from rejected revision" };
    }

    // Check if there are subsequent non-rejected revisions
    const sortedRevisions = [...allRevisions].sort((a, b) => {
      const revA = parseRevision(a.rev);
      const revB = parseRevision(b.rev);
      if (!revA && !revB) return 0;
      if (!revA) return 1;
      if (!revB) return -1;
      if (revA.type === "special") return -1;
      if (revB.type === "special") return 1;
      if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
      if (revA.type === "major" && revB.type !== "major") return -1;
      if (revB.type === "major" && revA.type !== "major") return 1;
      if (revA.minorNum !== revB.minorNum) return revA.minorNum - revB.minorNum;
      return revA.amendChar.localeCompare(revB.amendChar);
    });

    const currentIndex = sortedRevisions.findIndex(
      (rev) => rev.procedure_revision_id === currentRevision.procedure_revision_id
    );

    if (currentIndex === -1) {
      return { canCreate: true, reason: "Current revision not found in sorted list" };
    }

    const subsequentRevisions = sortedRevisions.slice(currentIndex + 1);
    const nonRejectedSubsequent = subsequentRevisions.filter((rev) => {
      const status = rev.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();
      return status !== "rejected" && status !== "maintained outside of system";
    });

    if (nonRejectedSubsequent.length === 0) {
      return { canCreate: true, reason: "No subsequent revisions exist, can create any revision type" };
    }

    // Check if all subsequent revisions are rejected
    const allSubsequentRejected = subsequentRevisions.every((rev) => {
      const status = rev.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();
      return status === "rejected";
    });

    if (allSubsequentRejected) {
      return { canCreate: true, reason: "All subsequent revisions are rejected, can create any revision type" };
    }

    // If there are active subsequent revisions, only allow amendments
    return { canCreate: true, reason: "Can create amendment only (non-rejected revisions below)" };
  };

  // Handle radio button change (single selection)
  const handleRadioChange = (value, label) => {
    setSelectedRevision({ value, label });
    setRevision(value);
  };

  // Get next major revision
  const getNextMajorRevision = (revisions) => {
    if (!revisions || revisions.length === 0) return "A-";
    
    const majorRevisions = revisions
      .map(rev => parseRevision(rev.rev))
      .filter(parsed => parsed && parsed.type === "major")
      .sort((a, b) => a.base.localeCompare(b.base));
    
    if (majorRevisions.length === 0) return "A-";
    
    const lastMajor = majorRevisions[majorRevisions.length - 1];
    const nextLetter = String.fromCharCode(lastMajor.base.charCodeAt(0) + 1);
    return `${nextLetter}-`;
  };

  // Get next minor revision for the latest major
  const getNextMinorRevision = (revisions) => {
    if (!revisions || revisions.length === 0) return "A1";
    
    // Find the latest major revision
    const majorRevisions = revisions
      .map(rev => parseRevision(rev.rev))
      .filter(parsed => parsed && parsed.type === "major")
      .sort((a, b) => a.base.localeCompare(b.base));
    
    if (majorRevisions.length === 0) return "A1";
    
    const latestMajor = majorRevisions[majorRevisions.length - 1];
    const latestMajorBase = latestMajor.base;
    
    // Find all minor revisions for this major
    const minorRevisions = revisions
      .map(rev => parseRevision(rev.rev))
      .filter(parsed => parsed && parsed.type === "minor" && parsed.base === latestMajorBase)
      .sort((a, b) => a.minorNum - b.minorNum);
    
    const nextMinorNum = minorRevisions.length === 0 ? 1 : minorRevisions[minorRevisions.length - 1].minorNum + 1;
    return `${latestMajorBase}${nextMinorNum}`;
  };

  // Get next amendment revision
  const getNextAmendmentRevision = (revisions) => {
    if (!revisions || revisions.length === 0) return "A1a";
    
    // Find the latest minor revision
    const minorRevisions = revisions
      .map(rev => parseRevision(rev.rev))
      .filter(parsed => parsed && parsed.type === "minor")
      .sort((a, b) => {
        if (a.base !== b.base) return a.base.localeCompare(b.base);
        return a.minorNum - b.minorNum;
      });
    
    if (minorRevisions.length === 0) return "A1a";
    
    const latestMinor = minorRevisions[minorRevisions.length - 1];
    const latestMinorBase = `${latestMinor.base}${latestMinor.minorNum}`;
    
    // Find all amendments for this minor
    const amendmentRevisions = revisions
      .map(rev => parseRevision(rev.rev))
      .filter(parsed => parsed && parsed.type === "amend" && 
                        `${parsed.base}${parsed.minorNum}` === latestMinorBase)
      .sort((a, b) => a.amendChar.localeCompare(b.amendChar));
    
    const nextAmendChar = amendmentRevisions.length === 0 ? "a" : 
                         String.fromCharCode(amendmentRevisions[amendmentRevisions.length - 1].amendChar.charCodeAt(0) + 1);
    return `${latestMinorBase}${nextAmendChar}`;
  };

  // Generate revision options
  const generateRevisionOptions = () => {
    if (revisionData.length === 0) {
      return (
        
        <label
          style={{
            display: "flex",
            gap: "5px",
          }}
        >
          <input
            type="radio"
            name="revision"
            value="A-"
            checked={revision === "A-"}
            onChange={() => setRevision("A-")}
          />
          <span style={{ fontSize: "12px" }}>A- (Major)</span>
        </label>
      );
    }

    const groupBySeries = (revisions) => {
      const series = {};
      for (const rev of revisions) {
        const parsed = parseRevision(rev.rev);
        if (!parsed) continue;
        const majorKey = parsed.base;
        const minorKey = `${parsed.base}${parsed.minorNum}`;
        series[majorKey] ??= [];
        series[majorKey].push(rev);
        if (parsed.type === "minor" || parsed.type === "amend") {
          series[minorKey] ??= [];
          series[minorKey].push(rev);
        }
      }
      return series;
    };

    const allRejected = (revList = []) =>
      revList.length > 0 &&
      revList.every(
        (r) =>
          r.status_changes?.[0]?.status_changed_to?.toLowerCase() ===
          "rejected"
      );

    const sortedRevisions = [...revisionData].sort((a, b) => {
      const revA = parseRevision(a.rev);
      const revB = parseRevision(b.rev);
      if (!revA && !revB) return 0;
      if (!revA) return 1;
      if (!revB) return -1;
      if (revA.type === "special") return -1;
      if (revB.type === "special") return 1;
      if (revA.base !== revB.base)
        return revA.base.localeCompare(revB.base);
      if (revA.type === "major" && revB.type !== "major")
        return -1;
      if (revB.type === "major" && revA.type !== "major")
        return 1;
      if (revA.minorNum !== revB.minorNum)
        return revA.minorNum - revB.minorNum;
      return revA.amendChar.localeCompare(revB.amendChar);
    });

    const lastValidRevision = [...sortedRevisions]
      .reverse()
      .find(
        (rev) =>
          !["rejected", "external"].includes(
            rev.status_changes?.[0]?.status_changed_to?.toLowerCase() ||
              ""
          )
      );

    if (!lastValidRevision) return null;

    const lastRevParsed = parseRevision(
      lastValidRevision.rev || "A0"
    );
    if (!lastRevParsed) return null;

    const fromRevStatus =
      lastValidRevision.status_changes?.[0]?.status_changed_to?.toLowerCase() ||
      "";

    const isReleasedOrArchived = [
      "released",
      "archived",
    ].includes(fromRevStatus);

    const getNextLetter = (str) => {
      const lastChar = str[str.length - 1];
      const rest = str.slice(0, -1);
      const nextChar = String.fromCharCode(
        lastChar.charCodeAt(0) + 1
      );
      if (lastChar === "Z") return rest + "A";
      return rest + nextChar;
    };

    // Find the highest major revision base that exists
    const findHighestMajorBase = () => {
      const majorBases = revisionData
        .map((rev) => {
          const parsed = parseRevision(rev.rev);
          return parsed && parsed.type === "major"
            ? parsed.base
            : null;
        })
        .filter((base) => base !== null)
        .sort();

      if (majorBases.length === 0) {
        // No major revisions exist, start with A
        return "A";
      }

      // Return the highest major base
      return majorBases[majorBases.length - 1];
    };

    const highestMajorBase = findHighestMajorBase();
    const nextMajorBase = getNextLetter(highestMajorBase);
    const majorRev = `${nextMajorBase}-`;

    // Get the current revision being viewed for amendment generation
    const currentRevisionForAmend = revisionData.find(
      (rev) => rev.procedure_revision_id === currentRev.procedure_revision_id
    );
    const currentRevParsed = parseRevision(
      currentRevisionForAmend?.rev
    );

    // Find the next available minor number for the current revision's base
    const findNextMinorNumber = () => {
      const base = currentRevParsed?.base;
      if (!base) return 1;

      // Get all existing minor revisions for this base
      const existingMinors = revisionData
        .filter((rev) => {
          const parsed = parseRevision(rev.rev);
          return (
            parsed &&
            parsed.base === base &&
            parsed.type === "minor"
          );
        })
        .map((rev) => parseRevision(rev.rev).minorNum)
        .sort((a, b) => a - b);

      if (existingMinors.length === 0) {
        // No existing minors, start with 1
        return 1;
      }

      // Find the next number after the highest existing minor
      return existingMinors[existingMinors.length - 1] + 1;
    };

    const nextMinorNum = findNextMinorNumber();
    const minorRev = `${
      currentRevParsed?.base || lastRevParsed.base
    }${nextMinorNum}`;

    // Find the next available amendment character
    const findNextAmendmentChar = () => {
      const base = currentRevParsed?.base;
      const minorNum = currentRevParsed?.minorNum;
      // If current revision is an amendment, we need to find the next amendment in the sequence
      if (currentRevParsed?.type === "amend_minorless") {
        // Get all existing amendments for this base (A-a, A-b, etc.)
        const existingAmendments = revisionData
          .filter((rev) => {
            const parsed = parseRevision(rev.rev);
            return (
              parsed &&
              parsed.base === base &&
              parsed.type === "amend_minorless" &&
              parsed.minorNum === 0
            );
          })
          .map((rev) => parseRevision(rev.rev).amendChar)
          .sort();

        if (existingAmendments.length === 0) {
          // No existing amendments, start with 'a'
          return "a";
        }

        // Find the next character after the highest existing amendment
        const lastChar =
          existingAmendments[existingAmendments.length - 1];
        const nextChar = String.fromCharCode(
          lastChar.charCodeAt(0) + 1
        );
        return nextChar;
      } else if (currentRevParsed?.type === "major") {
        // For major revisions, get all amendments for this base (A-a, A-b, etc.)
        const existingAmendments = revisionData
          .filter((rev) => {
            const parsed = parseRevision(rev.rev);
            return (
              parsed &&
              parsed.base === base &&
              parsed.type === "amend_minorless" &&
              parsed.minorNum === 0
            );
          })
          .map((rev) => parseRevision(rev.rev).amendChar)
          .sort();

        if (existingAmendments.length === 0) {
          // No existing amendments, start with 'a'
          return "a";
        }

        // Find the next character after the highest existing amendment
        const lastChar =
          existingAmendments[existingAmendments.length - 1];
        return String.fromCharCode(lastChar.charCodeAt(0) + 1);
      } else {
        // For minor revisions, get amendments for this base and minor number
        const existingAmendments = revisionData
          .filter((rev) => {
            const parsed = parseRevision(rev.rev);
            return (
              parsed &&
              parsed.base === base &&
              parsed.minorNum === minorNum &&
              parsed.type === "amend"
            );
          })
          .map((rev) => parseRevision(rev.rev).amendChar)
          .sort();

        if (existingAmendments.length === 0) {
          // No existing amendments, start with 'a'
          return "a";
        }

        // Find the next character after the highest existing amendment
        const lastChar =
          existingAmendments[existingAmendments.length - 1];
        return String.fromCharCode(lastChar.charCodeAt(0) + 1);
      }
    };

    const amendCharNext = findNextAmendmentChar();
    const amendRev =
      currentRevParsed?.type === "major"
        ? `${currentRevParsed.base}-${amendCharNext}`
        : currentRevParsed?.type === "amend_minorless"
        ? `${currentRevParsed.base}-${amendCharNext}`
        : `${currentRevParsed.base}${currentRevParsed.minorNum}${amendCharNext}`;

    const existingRevs = revisionData.map((r) => r.rev);

    const series = groupBySeries([...revisionData]);
    const majorSeriesKey = lastRevParsed.base;
    const minorSeriesKey = `${lastRevParsed.base}${lastRevParsed.minorNum}`;

    const allowMinor = !allRejected(series[majorSeriesKey] || []);

    // Check if we can allow amendments based on the CURRENT revision's series
    // (not just the last revision in the list)
    const currentMajorSeriesKey = currentRevParsed?.base;
    const currentMinorSeriesKey = currentRevParsed 
      ? `${currentRevParsed.base}${currentRevParsed.minorNum}`
      : null;
    
    // Allow amendments if:
    // 1. Current revision is a minor revision and its series is not all rejected, OR
    // 2. Current revision is an amendment (we'll check if it's last in series via canCreateRevision)
    const allowAmend = 
      (currentRevParsed?.type === "minor" && currentMinorSeriesKey && !allRejected(series[currentMinorSeriesKey] || [])) ||
      (currentRevParsed?.type === "amend" || currentRevParsed?.type === "amend_minorless");
    
    const options = [];

    // Get the current revision being viewed
    const currentRevisionForLogic = revisionData.find(
      (rev) => rev.procedure_revision_id === currentRev.procedure_revision_id
    );

    // Use the canCreateRevision function to determine what types can be created
    const { canCreate, reason } = canCreateRevision(
      currentRevisionForLogic,
      revisionData
    );

    if (!canCreate) {
      // If revision creation is not allowed, show error message
      return (
        <div style={{ color: "red", fontSize: "12px" }}>
          {reason}
        </div>
      );
    }

    // Helper function to check if any subsequent major series has all rejected revisions
    const hasSubsequentMajorSeriesWithAllRejected = (
      currentRevParsed,
      allRevisions
    ) => {
      // Get all revisions that come after the current revision
      const sortedRevisions = [...allRevisions].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base)
          return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major")
          return -1;
        if (revB.type === "major" && revA.type !== "major")
          return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const currentRevisionIndex = sortedRevisions.findIndex(
        (rev) =>
          rev.procedure_revision_id ===
          currentRevisionForLogic.procedure_revision_id
      );

      if (currentRevisionIndex === -1) return false;

      const subsequentRevisions = sortedRevisions.slice(
        currentRevisionIndex + 1
      );

      // Get all unique major series from subsequent revisions
      const subsequentMajorSeries = new Set();
      subsequentRevisions.forEach((rev) => {
        const parsed = parseRevision(rev.rev);
        if (parsed && parsed.base !== currentRevParsed.base) {
          subsequentMajorSeries.add(parsed.base);
        }
      });

      // Check if any of these major series have all their revisions rejected
      for (const majorSeries of subsequentMajorSeries) {
        const revisionsInMajorSeries = subsequentRevisions.filter(
          (rev) => {
            const parsed = parseRevision(rev.rev);
            return parsed && parsed.base === majorSeries;
          }
        );

        if (
          revisionsInMajorSeries.length > 0 &&
          revisionsInMajorSeries.every((rev) => {
            const status = rev?.status_changes
              ?.find((sc) => sc.is_latest)
              ?.status_changed_to?.toLowerCase();
            return status === "rejected";
          })
        ) {
          return true;
        }
      }

      return false;
    };

    // Check if any subsequent major series has all rejected revisions
    const hasRejectedMajorSeries =
      hasSubsequentMajorSeriesWithAllRejected(
        currentRevParsed,
        revisionData
      );

    // Check if we can create major revisions
    const canCreateMajor =
      reason.includes("multiple revision types") ||
      reason.includes("any revision type") ||
      reason.includes("No subsequent revisions exist") ||
      reason.includes("All subsequent revisions are rejected") ||
      reason.includes(
        "Can create next major, minor, or amended revision from last non-rejected revision (all below are rejected)"
      ) ||
      reason.includes(
        "Can create next major, minor, or amended revision from last amended revision (no non-rejected below)"
      );

    // Check if we can create minor revisions
    const canCreateMinor =
      (reason.includes("multiple revision types") ||
        reason.includes("any revision type") ||
        reason.includes("No subsequent revisions exist") ||
        reason.includes(
          "All subsequent revisions are rejected"
        ) ||
        reason.includes(
          "Can create next major, minor, or amended revision from last non-rejected revision (all below are rejected)"
        ) ||
        reason.includes(
          "Can create next major, minor, or amended revision from last amended revision (no non-rejected below)"
        )) &&
      !hasRejectedMajorSeries; // Don't allow minor if any subsequent major series is all rejected

    // Check if we can create amendments (only from Released or Archived revisions)
    const currentRevisionStatus =
      currentRevisionForLogic?.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();

    // For amendments, we need to be more specific about when to apply the rejected major series restriction
    // Only apply it when we're not in the "amendment only" case (which means there are active subsequent revisions)
    // OR when we're not in the "All subsequent revisions are rejected" case
    const shouldBlockAmendmentDueToRejectedSeries =
      hasRejectedMajorSeries &&
      !reason.includes("amendment only") &&
      !reason.includes("All subsequent revisions are rejected") &&
      !reason.includes(
        "Can create next major, minor, or amended revision from last non-rejected revision (all below are rejected)"
      ) &&
      !reason.includes(
        "Can create amendment only (non-rejected revisions below)"
      ) &&
      !reason.includes(
        "Can create next major, minor, or amended revision from last amended revision (no non-rejected below)"
      );

    // Check if current revision is an amendment
    const isCurrentRevisionAmendment = 
      currentRevParsed?.type === "amend" || 
      currentRevParsed?.type === "amend_minorless";

    // Only allow amendments if:
    // 1. Current revision status is "released" or "archived" (for non-amendment revisions)
    // 2. OR current revision is an amendment itself AND it's the last non-rejected in its series
    //    (allow creating next amendment from amendment if it's the last non-rejected in series, regardless of status, as long as not rejected)
    const canCreateAmendment =
      (reason.includes("amendment only") ||
        reason.includes("any revision type") ||
        reason.includes(
          "All subsequent revisions are rejected"
        ) ||
        reason.includes(
          "Can create next amendment from last amended revision"
        ) ||
        reason.includes(
          "most recent non-rejected amended or released/approved revision"
        ) ||
        reason.includes(
          "Can create amendment only (non-rejected revisions below)"
        ) ||
        reason.includes(
          "Can create next major, minor, or amended revision from last amended revision (no non-rejected below)"
        ) ||
        reason.includes(
          "Can create amendment from released/archived"
        ) ||
        reason.includes(
          "Can create next amendment only"
        ) ||
        reason.includes(
          "last non-rejected in series"
        )) &&
      ((currentRevisionStatus === "released" || currentRevisionStatus === "archived") || // For non-amendment revisions
       (isCurrentRevisionAmendment && currentRevisionStatus !== "rejected")) && // For amendment revisions, allow if not rejected and it's the last non-rejected in its series
      !shouldBlockAmendmentDueToRejectedSeries;

    // Add Major revision option if allowed
    if (canCreateMajor) {
      options.push({
        label: `${majorRev} (Major)`,
        value: majorRev,
      });
    }

    // Add Minor revision option if allowed
    if (
      canCreateMinor &&
      (allowMinor ||
        reason.includes("All subsequent revisions are rejected"))
    ) {
      options.push({
        label: `${minorRev} (Minor)`,
        value: minorRev,
      });
    }

    // Add Amendment option if allowed
 
    
    if (
      canCreateAmendment &&
      !existingRevs.includes(amendRev) &&
      (allowAmend ||
        reason.includes(
          "All subsequent revisions are rejected"
        ) ||
        reason.includes("any revision type") ||
        reason.includes("last non-rejected in series") ||
        reason.includes("amendment only") ||
        reason.includes("Can create amendment from released/archived"))
    ) {
      options.push({
        label: `${amendRev} (Amend)`,
        value: amendRev,
      });
    }

    if (options.length > 0 && revision === "A") {
      setRevision(options[0].value);
    }

    return options.map((opt) => (
      <label
        key={opt.value}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12px",
          marginBottom: "6px",
          whiteSpace: "nowrap",
          width: "40%",
        }}
      >
        <input
          type="radio"
          name="revision"
          value={opt.value}
          checked={selectedRevision?.value === opt.value}
          onChange={() => handleRadioChange(opt.value, opt.label)}
          style={{ 
            marginRight: "4px",
            transform: "scale(0.9)"
          }}
        />
        {opt.label}
      </label>
    ));
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: "8px",
      }}
    >
      {/* Left Side: Label + Radio Buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          width: "71%",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            marginRight: "4px",
            marginLeft: "10px"
          }}
        >
          Revision:
        </span>
        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "center",
          }}
        >
          {generateRevisionOptions()}
        </div>
      </div>
  
      {/* Right Side: Message */}
      <div
        style={{
          width: "30%",
          paddingLeft: "6px",
        }}
      >
        <p
          style={{
            fontSize: "10px",
            color: "#0D6EFD",
            margin: 0,
            marginRight: "-10px",
            lineHeight: "1.4",
            textAlign: "right",
          }}
        >
          {(() => {
            // Get the latest status change
            const latestStatusChange = currentRev?.status_changes?.find(
              (sc) => sc.is_latest
            );
            let status = latestStatusChange?.status_changed_to || "Unknown";
            
            // Check for pending approvals (show "Submitted For Approval" if approved but has pending approvals)
            const hasPendingApprovals = currentRev?.required_by?.some(
              (req) => req.purpose?.toLowerCase() === "approve" && req.is_open
            );
            if (
              status.toLowerCase() === "approved" &&
              hasPendingApprovals
            ) {
              status = "Submitted For Approval";
            }
            
            // Get the date from the status change or revision
            const revisionDate = latestStatusChange?.created_at || currentRev?.created_at;
            
            return (
              <>
                Rev {selectedRevision?.value || revision} will be based on Rev{" "}
                {myRevision} when {status}, at{" "}
                {revisionDate ? convertUtcToFormattedTime(revisionDate) : ""}
              </>
            );
          })()}
        </p>
      </div>
    </div>
  );
  
};

export default RevisionSelection;
